import { type Dispatch, type FC, type ReactNode, type RefObject, useEffect, useRef, useState } from 'react';

import { useForm } from 'react-hook-form';
import { ErrorMessage } from '@hookform/error-message';

import { _isEmpty, _notNil, _isNotEmpty } from '@/littledash';

import type { MetadataField } from '@/model/Metadata.model';
import { MetadataTypes } from '@/model/Metadata.model';
import type { Study, StudyMetrics } from '@/model/Study.model';

import { preventNumberScroll } from '@/helpers';
import { RouteService } from '@/support/RouteService';

import Button from '@/components/UI/Button';
import type { SpreadsheetV16Settings } from '@/components/UI/SpreadSheet/SpreadsheetV16.model';
import type { HotTableRef } from '@/components/UI/SpreadSheet/HotTableV16';

import { getMetaData } from '../Colony.utils';

import CagesTableV16 from './CagesTableV16';

const newOverlay: ReactNode = (
  <>
    <h3 className="mb2">Cages and metadata will appear here</h3>
    <p style={{ maxWidth: '520px', alignSelf: 'center' }}>
      Enter the number of cages you will be adding in the text box above. You can continue to add additional cages the
      same way afterwards.
    </p>
  </>
);
const MAX_CAGES = 100;

export interface CagesV16State {
  cages: {
    settingsV16: SpreadsheetV16Settings;
    data: Array<Record<string, unknown>>;
    metadata: Array<MetadataField>;
    metaGlossaries: Array<MetadataField>;
    cellErrors: Record<string, string>;
    overlay: ReactNode;
    cageCount: number;
    maxRows: number;
  };
}

export interface CagesV16Action {
  type: string;
  id?: string;
  data?: Record<string, unknown> | string;
}

type StudyWithCounts = Study & Partial<StudyMetrics>;

interface CagesV16Props {
  state: CagesV16State;
  dispatch: Dispatch<CagesV16Action>;
  study?: StudyWithCounts;
}

interface CagesV16FormValues {
  cage_amount: number;
}

const CagesV16: FC<CagesV16Props> = ({ state, dispatch, study }) => {
  const {
    cages: { settingsV16, data: cageData, metadata, cellErrors, overlay, cageCount, maxRows },
  } = state;

  const [isInvalid, setIsInvalid] = useState<boolean>(false);
  const ref = useRef<HotTableRef>(null);

  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm<CagesV16FormValues>({
    defaultValues: { cage_amount: 5 },
  });

  // --- dispatch helpers ---

  const updateMetadata = (updatedMetadata: Array<MetadataField>): void => {
    dispatch({
      type: 'update',
      id: 'cages',
      data: { metadata: updatedMetadata },
    });
  };
  const updateGlossaries = (metaGlossaries: Array<MetadataField>): void => {
    dispatch({
      type: 'update',
      id: 'cages',
      data: { metaGlossaries },
    });
  };
  const updateOverlay = (updatedOverlay: ReactNode): void => {
    dispatch({
      type: 'update',
      id: 'cages',
      data: { overlay: updatedOverlay },
    });
  };
  const updateApiError = (apiError: unknown): void => {
    dispatch({
      type: 'update',
      id: 'cages',
      data: apiError as Record<string, unknown>,
    });
  };
  const updateMaxRows = (updatedMaxRows: number): void => {
    dispatch({
      type: 'update',
      id: 'cages',
      data: { maxRows: updatedMaxRows },
    });
  };
  const updateCellErrors = (updatedCellErrors: Record<string, string>): void => {
    dispatch({
      type: 'update',
      id: 'cages',
      data: { cellErrors: updatedCellErrors },
    });
  };
  const updateCageCount = (updatedCageCount: number): void => {
    dispatch({
      type: 'update',
      id: 'cages',
      data: { cageCount: updatedCageCount },
    });
  };
  const updateSettingsV16 = (updated: SpreadsheetV16Settings): void => {
    dispatch({
      type: 'update',
      id: 'cages',
      data: { settingsV16: { ...updated } },
    });
  };

  const applyInvalidError = (currentErrors: Record<string, string>): void =>
    updateCellErrors({ ...currentErrors, 'auto-validation': 'There are invalid cells' });

  // --- callbacks ---

  const onSubmit = ({ cage_amount }: CagesV16FormValues): void => {
    updateMaxRows(maxRows + cage_amount);
  };

  const afterContextMenuHide = (): void => {
    if (maxRows >= cageData.length) {
      updateMaxRows(cageData.length);
    }
  };

  const afterRemoveRow = (): void => {
    if (_isEmpty(cageData)) {
      updateOverlay(newOverlay);
    }
    const hot = ref.current?.hotInstance;
    const sourceData = hot?.getSourceData();

    if (_isEmpty(sourceData)) {
      updateCellErrors({ 'no-cage': 'You must supply at least 1 cage' });
    }
  };

  // --- effects ---

  useEffect(() => {
    getMetaData(MetadataTypes.collection_meta)
      .then((response) => {
        const data = response?.body ?? [];
        updateGlossaries(data as unknown as Array<MetadataField>);
        if (_notNil(settingsV16?.columns)) {
          const metadataColumnIds = settingsV16.columns.reduce<Array<unknown>>((acc, c) => {
            if (_notNil(c.metaID)) {
              acc.push(c.metaID);
            }
            return acc;
          }, []);
          const updatedMetadata = (data as unknown as Array<MetadataField>).filter(
            (d) => !metadataColumnIds.includes(d.id)
          );
          dispatch({ type: 'stepReady' });
          return updateMetadata(updatedMetadata);
        }
        dispatch({ type: 'stepReady' });
        return updateMetadata(data as unknown as Array<MetadataField>);
      })
      .catch((error: unknown) => updateApiError(error));
  }, []);

  useEffect(() => {
    if (_isEmpty(cageData)) {
      updateOverlay(newOverlay);
    }
  }, [cageData]);

  useEffect(() => {
    if (maxRows > 0 && maxRows > cageData.length) {
      const { 'no-cage': _noCage, ...remainingErrors } = cellErrors;
      void _noCage;
      if (isInvalid) {
        applyInvalidError(remainingErrors);
      } else {
        updateCellErrors(remainingErrors);
      }
      updateOverlay('');

      const hot = ref.current?.hotInstance;

      let cageNameNumber = cageData.length !== 0 ? cageCount : 1;

      if (_isEmpty(cageData) && _notNil(study) && _notNil(study.counts?.cages_with_trashed)) {
        cageNameNumber += study.counts!.cages_with_trashed;
      }

      const rowAmountToAdd = maxRows - cageData.length;

      const insertIndex = cageData.length > 0 ? cageData.length - 1 : 0;
      hot?.batch(() => {
        hot.alter('insert_row_below', insertIndex, rowAmountToAdd, 'insertRows');
        const totalRows = hot.countRows();
        const startRow = Math.max(0, totalRows - rowAmountToAdd);
        const allChanges: Array<[number, number, number | string]> = [];
        let currentRow = startRow;
        let batchCageName = cageNameNumber;
        for (let i = 0; i < rowAmountToAdd; i++) {
          allChanges.push([currentRow, 0, batchCageName]);
          allChanges.push([currentRow, 1, `Cage ${batchCageName}`]);
          currentRow++;
          batchCageName++;
        }
        hot.setDataAtCell(allChanges, 'setCageID');
        cageNameNumber = batchCageName;
      });
      updateCageCount(cageNameNumber);
    }
  }, [maxRows]);

  useEffect(() => {
    if (isInvalid) {
      applyInvalidError(cellErrors);
    } else {
      const { 'auto-validation': _autoVal, ...remainingErrors } = cellErrors;
      void _autoVal;
      updateCellErrors(remainingErrors);
    }
  }, [isInvalid]);

  // --- render ---

  const TableComponent = (
    <div className="ow-spreadsheet-styles">
      <CagesTableV16
        data={cageData}
        innerRef={ref as RefObject<HotTableRef>}
        settings={settingsV16}
        setSettings={updateSettingsV16}
        metadata={metadata}
        setMetadata={updateMetadata}
        setInvalid={setIsInvalid}
        overlay={overlay}
        maxRows={maxRows}
        afterRemoveRow={afterRemoveRow}
        afterContextMenuHide={afterContextMenuHide}
      />
    </div>
  );

  return (
    <>
      <form data-test-component="CagesV16" data-test-element="form" onSubmit={handleSubmit(onSubmit)}>
        <div className="flex-row items-start flex flex-wrap mb4">
          <p className="lh-copy set-width">
            <span>Enter the amount of cages, animals per cage and any metadata to continue. </span>
            <a
              href={RouteService.help('registering_animals')}
              target="_blank"
              rel="noopener noreferrer"
              className="dib link blue"
            >
              Read more.
            </a>
          </p>
        </div>
        <label htmlFor="cage_amount" className="near-black">
          How many more cages would you like to add?
        </label>
        <div className="flex db mt2 w2">
          <input
            id="cage_amount"
            type="number"
            onWheel={preventNumberScroll}
            placeholder="5"
            data-testid="cage-amount-input"
            {...register('cage_amount', {
              valueAsNumber: true,
              required: 'You must supply a numerical amount of cages to create.',
              max: {
                value: MAX_CAGES,
                message: `You have exceeded the maximum cage limit of ${MAX_CAGES}.`,
              },
              validate: {
                numerical: (value) => (Number.isInteger(value) && value >= 0) || 'Please only input numerical values.',
                totalLimit: (value) =>
                  Number.isNaN(value) ||
                  cageData.length + value <= MAX_CAGES ||
                  `Cannot add more than ${MAX_CAGES} cages`,
              },
            })}
          />
          <Button submit className="ml3" disabled={_isNotEmpty(errors)} plain testId="add-cages-btn">
            Add Cages
          </Button>
        </div>
        <ErrorMessage
          errors={errors}
          name="cage_amount"
          render={({ message }) => (
            <p className="f6 red db" data-testid="cage-amount-error">
              {message}
            </p>
          )}
        />
      </form>

      {TableComponent}
    </>
  );
};
export default CagesV16;
